import React, { useState, useRef, useEffect } from 'react'
import AutoResizeTextarea from '../shared/AutoResizeTextarea'
import { createCommunityPost } from 'utils/teachingTeamApi'
import { compressImages } from 'utils/imageCompressor'

const MAX_IMAGES = 4

const TEACHER_PROMPTS_DEFAULT = [
  '跟學生分享最近的教學心得吧',
  '有什麼課程更新想讓大家知道？',
  '分享一個學習資源給學生吧',
]
const STUDENT_PROMPTS_DEFAULT = [
  '最近學到什麼？',
  '上完課有什麼想法嗎？',
  '有什麼想問老師的？',
]

function pickRandom(arr) {
  return arr[Math.floor(Math.random() * arr.length)]
}

export default function CommunityPostComposer({ teamId, categories = [], currentUser, colors, onPostCreated, translations = {}, alwaysExpanded = false, isOwnerOrAdmin = false }) {
  const isTeacher = isOwnerOrAdmin || !!currentUser?.teamRole
  const teacherPrompts = translations.teacher_prompts || TEACHER_PROMPTS_DEFAULT
  const studentPrompts = translations.student_prompts || STUDENT_PROMPTS_DEFAULT
  const [placeholder] = useState(() => pickRandom(isTeacher ? teacherPrompts : studentPrompts))

  const [expanded, setExpanded] = useState(alwaysExpanded)
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [notifyStudents, setNotifyStudents] = useState(false)
  const [images, setImages] = useState([])
  const [imagePreviews, setImagePreviews] = useState([])
  const [submitting, setSubmitting] = useState(false)
  const [compressing, setCompressing] = useState(false)
  const [error, setError] = useState(null)
  const fileInputRef = useRef(null)
  const bodyRef = useRef(null)
  const imagePreviewsRef = useRef(imagePreviews)
  imagePreviewsRef.current = imagePreviews

  useEffect(() => {
    return () => { imagePreviewsRef.current.forEach(url => URL.revokeObjectURL(url)) }
  }, [])

  // In brand page: needs currentUser to show. In dashboard (alwaysExpanded): always show
  if (!currentUser && !alwaysExpanded) return null

  const avatarUrl = currentUser?.avatar_url
  const userName = currentUser?.name || 'User'
  const initials = userName.charAt(0).toUpperCase()

  const handleImageChange = async (e) => {
    const files = Array.from(e.target.files)
    e.target.value = ''
    const remaining = MAX_IMAGES - images.length
    const toAdd = files.slice(0, remaining)
    if (toAdd.length === 0) return

    setError(null)
    setCompressing(true)
    try {
      const compressed = await compressImages(toAdd)
      setImages((prev) => [...prev, ...compressed])
      setImagePreviews((prev) => [...prev, ...compressed.map((f) => URL.createObjectURL(f))])
    } catch (err) {
      setError('Failed to process image. Please try a different file.')
    } finally {
      setCompressing(false)
    }
  }

  const handleRemoveImage = (idx) => {
    URL.revokeObjectURL(imagePreviews[idx])
    setImages((prev) => prev.filter((_, i) => i !== idx))
    setImagePreviews((prev) => prev.filter((_, i) => i !== idx))
  }

  const handleCancel = () => {
    setExpanded(false)
    setTitle('')
    setBody('')
    setCategoryId('')
    setNotifyStudents(false)
    setError(null)
    imagePreviews.forEach(url => URL.revokeObjectURL(url))
    setImages([])
    setImagePreviews([])
  }

  const handleSubmit = async () => {
    if (!body.trim() || submitting || compressing) return
    setSubmitting(true)
    setError(null)
    try {
      const data = await createCommunityPost(teamId, {
        title: title.trim() || undefined,
        body: body.trim(),
        communityategoryId: categoryId || undefined,
        images: images.length > 0 ? images : undefined,
        notifyStudents: notifyStudents || undefined,
      })
      onPostCreated && onPostCreated(data.post || data)
      handleCancel()
    } catch (err) {
      setError(err.message || 'Failed to create post')
    } finally {
      setSubmitting(false)
    }
  }

  const handleExpand = () => {
    setExpanded(true)
    setTimeout(() => bodyRef.current?.focus(), 50)
  }

  // Collapsed: avatar + placeholder
  if (!expanded) {
    return (
      <div
        onClick={handleExpand}
        className="flex items-center gap-3 bg-white border border-gray-200 rounded-2xl px-4 py-3.5 cursor-pointer hover:border-gray-300 hover:shadow-sm transition-all"
      >
        <Avatar url={avatarUrl} initials={initials} colors={colors} />
        <span className="text-gray-400 flex-1">
          {placeholder}
        </span>
      </div>
    )
  }

  // Expanded: full form
  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
      {/* Title input — borderless, large */}
      <div className="px-5 pt-5">
        <input
          type="text"
          value={title}
          onChange={(e) => { setTitle(e.target.value); setError(null) }}
          placeholder={translations.title_placeholder || 'Title (optional)'}
          className="w-full text-lg font-medium text-gray-900 placeholder-gray-300 focus:outline-none"
        />
      </div>

      {/* Body textarea — borderless, auto-grow */}
      <div className="px-5 pt-2 pb-3">
        <AutoResizeTextarea
          ref={bodyRef}
          value={body}
          onChange={(e) => {
            setBody(e.target.value)
            setError(null)
          }}
          rows={3}
          placeholder={placeholder}
          className="w-full text-gray-700 placeholder-gray-300 focus:outline-none resize-none text-[15px] leading-relaxed"
          style={{ minHeight: '80px' }}
        />
      </div>

      {/* Image previews */}
      {(imagePreviews.length > 0 || compressing) && (
        <div className="px-5 pb-3 flex flex-wrap gap-2">
          {imagePreviews.map((url, idx) => (
            <div key={idx} className="relative group">
              <img src={url} alt="" className="w-20 h-20 object-cover rounded-lg" />
              <button
                onClick={() => handleRemoveImage(idx)}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-gray-800 text-white rounded-full text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                &times;
              </button>
            </div>
          ))}
          {compressing && (
            <div className="w-20 h-20 rounded-lg bg-gray-100 flex items-center justify-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-400" />
            </div>
          )}
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="mx-5 mb-3 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Bottom bar */}
      <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 bg-gray-50/50">
        <div className="flex items-center gap-1">
          {/* Image upload button */}
          {images.length < MAX_IMAGES && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handleImageChange}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={compressing}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                title={translations.add_images || 'Add images'}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z" />
                </svg>
              </button>
            </>
          )}

          {/* Category selector */}
          {categories.length > 0 && (
            <select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              className="text-sm text-gray-500 bg-transparent border-none focus:outline-none cursor-pointer px-2 py-1"
            >
              <option value="">{translations.no_category || 'Select a category'}</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          )}
        </div>

        <div className="flex items-center gap-3">
          {/* Notify students checkbox — owner/admin only */}
          {isOwnerOrAdmin && (
            <label className="flex items-center gap-1.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={notifyStudents}
                onChange={(e) => setNotifyStudents(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-3.5 h-3.5"
              />
              <span className="text-xs text-gray-500">{translations.notify_students || 'Email students'}</span>
            </label>
          )}
          <button
            onClick={handleCancel}
            className="text-sm text-gray-500 hover:text-gray-700 font-medium transition-colors"
          >
            {translations.cancel || 'CANCEL'}
          </button>
          <button
            onClick={handleSubmit}
            disabled={!body.trim() || submitting || compressing}
            className="px-5 py-1.5 text-sm font-semibold rounded-lg disabled:opacity-40 transition-colors"
            style={{
              backgroundColor: body.trim() && !compressing ? (colors?.primary || '#3366FF') : '#e5e7eb',
              color: body.trim() && !compressing ? '#fff' : '#9ca3af'
            }}
          >
            {submitting ? (translations.submitting || translations.publishing || 'POSTING...') : (translations.submit || translations.publish || 'POST')}
          </button>
        </div>
      </div>
    </div>
  )
}

function Avatar({ url, initials, colors }) {
  if (url) {
    return <img src={url} alt="" className="w-10 h-10 rounded-full object-cover" />
  }
  return (
    <div
      className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-sm"
      style={{ backgroundColor: colors?.primary || '#3366FF' }}
    >
      {initials}
    </div>
  )
}
